import clonedeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { errorCodes, getErrorMessageKey } from '../../../util/errorCodes';
import jsonPost from '../../../util/jsonPost';
import { labelsPropType, travelOptionsPackagePropType } from './PropTypes';

import {
  calculateTotalPricePerCurrency,
  findCurrencyItemFromListWithFallback,
  getInitialCurrencyState,
} from '../../../util/currency';
import Button from '../Atoms/Button';
import { getTotalPrice } from '../BookingFieldset/helpers';
import {
  StopoverTypes,
  getStopoverDataWithDataKey,
  getTourDatesWithStopoversConsidered,
  isStopoverOfType,
} from '../BookingStopoverOptions/helpers';
import {
  buildStopoverItineraryDescriptions,
  injectItineraryDescriptions,
} from '../BookingUpgrades/helpers';
import { withErrorBoundary } from '../Core/ErrorBoundary';
import ErrorMessage from '../Core/ErrorMessage';
import ItinerarySidebar from '../ItinerarySidebar';
import PackageSummary from '../PackageSummary';
import StickyComponent from '../StickyComponent';
import FlightClassGroup from './FlightClassGroup';
import FlightGroup from './FlightGroup';
import TransportGroup from './TransportGroup';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  apiURL: PropTypes.string.isRequired,
  labels: labelsPropType,
  package: travelOptionsPackagePropType.isRequired,
  selectedStep: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
});

const defaultProps = {
  labels: undefined,
};

const formatTitle = (prefix, suffix) => {
  if (!suffix || suffix === '') return prefix;
  return `${prefix}: ${suffix}`;
};

class BookingTravelOptions extends Component {
  constructor(props) {
    super(props);
    this.handleFlightOptionSelect = this.handleFlightOptionSelect.bind(this);
    this.handleItineraryToggleClick = this.handleItineraryToggleClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleTransportCardSelect = this.handleTransportCardSelect.bind(this);
    this.handleTransportDropdownChange = this.handleTransportDropdownChange.bind(this);
    this.handleFlightClassSelect = this.handleFlightClassSelect.bind(this);
    this.handleResetState = this.handleResetState.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);

    const showFlightsArray = [];

    // Initialising to show the Flight Classes when flights are required.
    for (let i = 0; i < props.package?.flights?.length; i += 1) {
      showFlightsArray.push(true);
    }

    const currentCurrency = getInitialCurrencyState(
      props.package?.pricePerPax[0]?.pricePerCurrency
    );

    const totalPricePerCurrency = calculateTotalPricePerCurrency(props.package);
    const currencyPriceSetting = findCurrencyItemFromListWithFallback(
      totalPricePerCurrency,
      currentCurrency.code
    );

    const state = {
      ...props.package?.selectedOptions,
      activeModalId: null,
      callToBook: false,
      errorMessage: null,
      isLoading: false,
      itinerary: props.package?.itinerarySideBar?.itinerary,
      itineraryUpdated: false,
      showFlightClasses: showFlightsArray,
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
    };

    state.totalPrice = getTotalPrice(state, props.package, currencyPriceSetting);

    this.state = state;
  }

  componentDidMount() {
    const { isFlightsOptional } = this.props.package;
    // If flights are optional, reset flights state using local storage and get correct total price.
    // This prevents flights from day 15 being mixed up with flights from day
    // 1 which results to an incorrect total price.
    if (!this.state.stopovers.length && isFlightsOptional) {
      if (!JSON.parse(sessionStorage.getItem('selectedFlights'))) return;
      this.setState({ flights: JSON.parse(sessionStorage.getItem('selectedFlights')) }, () => {
        sessionStorage.removeItem('selectedFlights');
        // Only display flight classes that are selected.
        const showFlightClasses = [];
        for (let i = 0; i < this.state.flights.length; i += 1) {
          if (this.state.flights[i] && Object.entries(this.state.flights[i]).length > 0) {
            showFlightClasses.push(true);
          } else {
            showFlightClasses.push(false);
          }
        }
        this.setState((state) => ({ totalPrice: this.getTotalPrice(state), showFlightClasses }));
      });
    }
    window.addEventListener('pageshow', this.handleResetState);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleResetState);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
    const newCurrencyPriceSetting = findCurrencyItemFromListWithFallback(
      this.props.package.pricePerCurrency,
      newCurrencyCode
    );
    if (newCurrencyPriceSetting)
      this.setState({
        currencyPriceSetting: newCurrencyPriceSetting,
        totalPrice: getTotalPrice(this.state, this.props.package, newCurrencyPriceSetting),
      });
    else console.error('Currency not found from current currency chosen to currency from model');
  }

  handleResetState() {
    this.setState({ isLoading: false });
  }

  /**
   * @param {string} groupKey
   * @param {number} groupIndex
   * @returns flightClassTourOption array
   */
  getFlightClasses(groupKey, groupIndex, stopoverDataKey) {
    // If flight is undefined null or empty return 0, so nothing will be done and no flight classes
    // will be searched.
    let selectedFlights;
    let flightOptions;

    // Use different schemas for stopover flights
    if (stopoverDataKey) {
      selectedFlights = this.state.stopovers.find(
        (selectedStopover) => selectedStopover.dataKey === stopoverDataKey
      )?.flights;
      flightOptions = getStopoverDataWithDataKey(
        this.props.package?.stopoverOptions,
        stopoverDataKey
      )?.flightOptions;
    } else {
      selectedFlights = this.state.flights;
      flightOptions = this.props.package?.flightOptions;
    }

    if (selectedFlights[groupIndex] === undefined || selectedFlights[groupIndex] === null) {
      return [];
    }
    if (Object.entries(selectedFlights[groupIndex]).length === 0) return [];

    const { optionKey } = selectedFlights[groupIndex];
    const { defaultOption = {}, upgrades = [] } =
      flightOptions?.find((option) => option.key === groupKey) || {};
    const allFlightTourOptions = [defaultOption, ...upgrades];
    const selectedFlight = allFlightTourOptions.find((flightTourOption) => {
      if (flightTourOption) return flightTourOption.option.key === optionKey;
      return false;
    });
    if (selectedFlight) {
      return selectedFlight.flightClasses;
    }
    return [];
  }

  handleFlightOptionSelect(groupIndex, optionKey, stopoverDataKey) {
    const mutableState = clonedeep(this.state);
    let selectedFlights;
    let flightOptions;
    // Use different schemas for stopover flights
    if (stopoverDataKey) {
      selectedFlights = mutableState.stopovers.find(
        (selectedStopover) => selectedStopover.dataKey === stopoverDataKey
      )?.flights;
      flightOptions = getStopoverDataWithDataKey(
        this.props.package?.stopoverOptions,
        stopoverDataKey
      )?.flightOptions?.[groupIndex];
    } else {
      selectedFlights = mutableState.flights;
      flightOptions = this.props.package?.flightOptions?.[groupIndex];
    }
    // Stopover flights are never optional
    if (!stopoverDataKey && selectedFlights.length && !this.props.package.isFlightsOptional) {
      if (selectedFlights[groupIndex]?.optionKey === optionKey) return;
    }
    const { defaultOption, upgrades } = flightOptions || {};
    const options = [defaultOption, ...upgrades];
    // Update selected flight class & itinerary
    const { option: flight, flightClasses } = options.find((option) => option.key === optionKey);
    // Update flights state differently if optional (only pass the optionally selected
    // flights) otherwise set the first flight in each group as selected.
    // Stopover flights are never optional
    if (!stopoverDataKey && this.props.package.isFlightsOptional) {
      // Get the group key so you can only select one flight in each group.
      const groupK = flightOptions?.key;
      // If a flight is preselected in a group, this saves the indexed
      // flight to update. If -1 then no pre selected flight.
      const alreadySelectedIndex = selectedFlights.findIndex((option) => {
        if (option) return option.groupKey === groupK;
        return false;
      });
      // if a group has a preselected flight update accordingly otherwise
      // push a new flight to the flights state.
      if (alreadySelectedIndex > -1) {
        // if the selected flight is selected again then it is removed otherwise
        // updates the flight and class key in the same group.
        if (selectedFlights[alreadySelectedIndex].optionKey === flight.key) {
          selectedFlights[alreadySelectedIndex] = {};
          mutableState.showFlightClasses[alreadySelectedIndex] = false;
        } else {
          selectedFlights[alreadySelectedIndex].optionKey = flight.key;
          selectedFlights[alreadySelectedIndex].flightClassKey = flightClasses.find(
            (x) => !x.soldOut
          ).key;
          mutableState.showFlightClasses[alreadySelectedIndex] = true;
        }
      } else {
        selectedFlights[groupIndex] = {
          optionKey: flight.key,
          flightClassKey: flightClasses.find((x) => !x.soldOut).key,
          groupKey: groupK,
        };
        mutableState.showFlightClasses[groupIndex] = true;
      }
    } else {
      if (!selectedFlights[groupIndex]) {
        selectedFlights[groupIndex] = {};
      }
      selectedFlights[groupIndex].flightClassKey =
        flightClasses.find((x) => !x.soldOut)?.key || null;
      selectedFlights[groupIndex].optionKey = optionKey;
      selectedFlights[groupIndex].groupKey = flightOptions.key;
    }

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleFlightClassSelect(groupIndex, optionKey, flightClassTourOptionKey, stopoverDataKey) {
    const mutableState = clonedeep(this.state);
    // Use different schemas for stopover flights
    let selectedFlights;
    if (stopoverDataKey) {
      selectedFlights = mutableState.stopovers.find(
        (selectedStopover) => selectedStopover.dataKey === stopoverDataKey
      )?.flights;
    } else {
      selectedFlights = mutableState.flights;
    }
    if (selectedFlights[groupIndex][0] === optionKey) return;
    selectedFlights[groupIndex].flightClassKey = flightClassTourOptionKey;

    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleTransportCardSelect(groupIndex, cardKey, optionKey) {
    const mutableState = clonedeep(this.state);
    if (mutableState.transports[groupIndex].optionKey === optionKey) return;
    const { key: groupKey } = this.props.package?.transportOptions?.[groupIndex] || {};
    mutableState.transports[groupIndex].optionKey = optionKey;
    mutableState.transports[groupIndex].groupKey = groupKey;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  handleTransportDropdownChange(groupIndex, groupKey, { target }) {
    const mutableState = clonedeep(this.state);
    if (mutableState.transports[groupIndex].optionKey === target.value) return;
    mutableState.transports[groupIndex].groupKey = groupKey;
    mutableState.transports[groupIndex].optionKey = target.value;
    mutableState.totalPrice = getTotalPrice(
      mutableState,
      this.props.package,
      this.state.currencyPriceSetting
    );
    mutableState.itineraryUpdated = true;
    this.setState(mutableState);
  }

  /**
   * Refactor Complete
   */
  handleItineraryToggleClick() {
    this.setState({ itineraryUpdated: false });
  }

  /**
   * Refactor Complete, although travel to be removed
   */
  handleSubmit() {
    const { flights, transports, stopovers, currentCurrencyCodeAndSymbol } = this.state;

    // Reset form errors and initialise loading status
    this.setState({
      errorMessage: null,
      isLoading: true,
    });

    // Not submitting empty objects.
    const filteredFlights = flights.filter((value) => Object.keys(value).length !== 0);

    // Post form data to API
    const options = {
      flights: filteredFlights,
      transports,
      stopovers,
      orderId: this.props.orderId,
      currency: currentCurrencyCodeAndSymbol.code,
    };
    return jsonPost(this.props.apiURL, options, (response) => {
      if (response.errorCode || response.errors) {
        if (response.errorCode === errorCodes.TourBookingTourHasReachedPaxCapLimit) {
          window.location.href = response.redirectUri;
        }
        const errorMessageKey = getErrorMessageKey(response.errorCode);
        return this.setState({
          errorMessage:
            this.props.labels[errorMessageKey] || response.message || 'An error occurred',
          isLoading: false,
        });
      }
      // TODO port to travel options
      // sessionStorage.setItem('selectedFlights', JSON.stringify(flights));
      window.location.href = response.redirectUri;
      return this.setState({ errorMessage: null });
    });
  }

  render() {
    const { flightOptions, transportOptions } = this.props.package;
    const { labels } = this.props;
    const { stopovers, flights } = this.state;

    const flightSets = [];
    const hasPreEventStopOvers = stopovers.find((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.PRE_EVENT)
    );
    const hasPostEventStopOvers = stopovers.find((stopover) =>
      isStopoverOfType(stopover, StopoverTypes.POST_EVENT)
    );

    if (!hasPreEventStopOvers) {
      flightSets.push({ flightOptions, selectedFlights: flights });
    }

    if (hasPreEventStopOvers || hasPostEventStopOvers) {
      stopovers.forEach((selectedStopover) => {
        const stopoverData = getStopoverDataWithDataKey(
          this.props.package.stopoverOptions,
          selectedStopover.dataKey
        );

        if (stopoverData?.flightOptions) {
          flightSets.push({
            stopoverDataKey: stopoverData.key,
            flightOptions: stopoverData?.flightOptions,
            selectedFlights: selectedStopover.flights,
          });
        }
      });
    }

    const itinerarySideBar = {
      ...this.props.package?.itinerarySideBar,
      isCallToBook: this.state.callToBook,
    };
    const { callToBookButtonText } = this.props.package?.itinerarySideBar || {};
    // Check if flights in state are empty and set showAtolLabel
    // accordingly (to show if flights are selected).
    const allFlights = flightSets.flatMap((flightSet) => flightSet.selectedFlights);
    const isFlightsEmpty =
      Object.values(allFlights).filter((value) => {
        if (value != null) return Object.entries(value).length !== 0;
        return false;
      }).length <= 0;
    const showAtolLabel = allFlights.length > 0 && !isFlightsEmpty;

    const { updatedTourFromDate, updatedTourToDate } = getTourDatesWithStopoversConsidered(
      this.props.package,
      stopovers
    );

    return (
      <section className="Booking">
        <form noValidate autoComplete="false" onSubmit={this.handleSubmit}>
          {/* Itinerary */}
          <ItinerarySidebar
            {...itinerarySideBar}
            fromDate={updatedTourFromDate}
            toDate={updatedTourToDate}
            handleToggleClick={this.handleItineraryToggleClick}
            hideBookButton
            isUpdated={this.state.itineraryUpdated}
            itinerary={injectItineraryDescriptions(
              this.state,
              this.props.package,
              this.props.selectedStep
            )}
            priceTotal={this.state.totalPrice}
            perPersonText={labels.perPersonText}
            preEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.PRE_EVENT
            )}
            postEventStopoverItinerary={buildStopoverItineraryDescriptions(
              this.props.package.stopoverOptions,
              this.state.stopovers,
              StopoverTypes.POST_EVENT
            )}
          />
          {/* Package summary */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              <StickyComponent contained>
                <PackageSummary
                  atolProtectedText={labels.atolProtectedText}
                  callToBookText={callToBookButtonText}
                  currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                  durationText={labels.durationText}
                  fromDate={updatedTourFromDate}
                  toDate={updatedTourToDate}
                  description={this.props.package?.tourDescription}
                  isCallToBook={this.state.callToBook}
                  preferredCardImage={this.props.labels.preferredCardImage}
                  price={this.state.totalPrice}
                  showAtolLabel={showAtolLabel}
                  title={this.props.package?.tourName}
                  totalPriceText={labels.totalPriceText}
                />
              </StickyComponent>
            </div>
          </div>
          {/* Flights */}
          {flightSets.map((flightSet) =>
            flightSet.flightOptions.map((flightGroup, groupIndex) => (
              <div className="Booking_group" key={flightGroup.key}>
                <h2 className="Booking_groupTitle">
                  {formatTitle(labels.flightTitle, flightGroup.subTitle)}
                </h2>
                <div className="Booking_item">
                  <FlightGroup
                    arrivalText={labels.flightArrivalText}
                    departureAirportText={labels.departureAirportText}
                    currencyPriceSetting={this.state.currencyPriceSetting}
                    currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                    departureText={labels.flightDepartureText}
                    groupIndex={groupIndex}
                    handleOptionSelect={this.handleFlightOptionSelect}
                    optionAddedText={labels.addedToPackageText}
                    options={[flightGroup.defaultOption, ...flightGroup.upgrades]}
                    selectedKey={
                      flightSet.selectedFlights[groupIndex] !== undefined
                        ? flightSet.selectedFlights[groupIndex].optionKey
                        : ''
                    }
                    soldOutText={labels.soldOutText}
                    stopoverDataKey={flightSet.stopoverDataKey}
                    perPersonText={labels.perPersonText}
                  />
                </div>
                {this.getFlightClasses(flightGroup.key, groupIndex, flightSet.stopoverDataKey)
                  .length > 0 && (
                  <div className="Booking_item">
                    <h3 className="Booking_groupSubtitle">
                      {formatTitle(labels.flightClassTitle, flightGroup.subTitle)}
                    </h3>
                    <FlightClassGroup
                      currencyPriceSetting={this.state.currencyPriceSetting}
                      currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                      optionKey={flightSet.selectedFlights[groupIndex].optionKey}
                      groupIndex={groupIndex}
                      handleOptionSelect={this.handleFlightClassSelect}
                      optionAddedText={labels.addedToPackageText}
                      options={this.getFlightClasses(
                        flightGroup.key,
                        groupIndex,
                        flightSet.stopoverDataKey
                      )}
                      selectedKey={
                        flightSet.selectedFlights[groupIndex] !== undefined
                          ? flightSet.selectedFlights[groupIndex].flightClassKey
                          : ''
                      }
                      soldOutText={labels.soldOutText}
                      stopoverDataKey={flightSet.stopoverDataKey}
                      perPersonText={labels.perPersonText}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          {/* Transport */}
          {transportOptions?.map((transportGroup, groupIndex) => (
            <div className="Booking_group" key={transportGroup.key}>
              <h2 className="Booking_groupTitle">
                {formatTitle(labels.transportTitle, transportGroup.subTitle)}
              </h2>
              <TransportGroup
                currencyPriceSetting={this.state.currencyPriceSetting}
                currencySymbol={this.state.currentCurrencyCodeAndSymbol.symbol}
                groupIndex={groupIndex}
                handleCardClick={this.handleTransportCardSelect}
                handleDropdownChange={this.handleTransportDropdownChange}
                inboundArrivalText={labels.transportInboundArrivalText}
                inboundDepartureText={labels.transportInboundDepartureText}
                outboundArrivalText={labels.transportOutboundArrivalText}
                outboundDepartureText={labels.transportOutboundDepartureText}
                transportGroupOptions={[transportGroup.defaultOption, ...transportGroup.upgrades]}
                selectedKeys={this.state.transports?.[groupIndex]}
                selectedText={labels.addedToPackageText}
                soldOutText={labels.soldOutText}
                perPersonText={labels.perPersonText}
              />
            </div>
          ))}
          {/* Submit */}
          <div className="Booking_group Booking_group-inset">
            <div className="Booking_content">
              {this.state.errorMessage && (
                <div className="Booking_item">
                  <ErrorMessage text={this.state.errorMessage} />
                </div>
              )}
              <div className="Booking_item Booking_item-center">
                {!this.state.callToBook && (
                  <Button
                    buttonType="button"
                    handleClick={this.handleSubmit}
                    isLoading={this.state.isLoading}
                    modifier="fill"
                    text={labels.submitButtonText}
                  />
                )}
                {this.state.callToBook && (
                  <div className="Booking_callToBook">{callToBookButtonText}</div>
                )}
              </div>
            </div>
          </div>
        </form>
      </section>
    );
  }
}

BookingTravelOptions.propTypes = propTypes;
BookingTravelOptions.defaultProps = defaultProps;
export default withErrorBoundary(BookingTravelOptions);
